import {
  Component,
  EventEmitter, OnDestroy,
  OnInit,
  Output, ViewChild,
} from '@angular/core';
import { PhoneService } from 'src/app/domain/login/service/phone.service';
import { PageType } from '../dto/types';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, of, Subject, timeout } from 'rxjs';
import { ButtonComponent } from '../../../shared/component/buttons/button.component';
import { catchError, map } from 'rxjs/operators';

@Component({
  selector: 'app-set-phone-form',
  templateUrl: './set-phone-form.component.html',
  styleUrls: [
    './set-phone-form.component.scss',
    '../entrance-group.scss',
  ],
})
export class SetPhoneFormComponent implements OnInit, OnDestroy {
  @Output() newPageEvent = new EventEmitter<PageType>();
  @ViewChild('verifyButton') verifyButton: ButtonComponent;
  private _destroyStream: Subject<void> = new Subject<void>();
  phoneForm: PhoneGroup;
  isVerifyDisabled = false;

  constructor(
    private phoneService: PhoneService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  changePage(page: PageType): void {
    this.newPageEvent.emit(page);
  }

  async onSubmit(): Promise<void> {
    if (this.isVerifyDisabled) {
      return;
    }
    this.disableVerifyButton();
    const phoneNumber = `+${this.phoneForm.controls.code.value}${this.phoneForm.value.phone}`;

    try {
      const verificationMethod = await this.phoneService.setPhone(phoneNumber);
      localStorage.setItem('maskedPhoneNumber', this.maskPhoneNumber(phoneNumber));
      localStorage.setItem('verificationMethod', verificationMethod);
      this.changePage('verifySms');
    } finally {
      this.enableVerifyButton();
    }
  }

  /**
   * Mask phone number with "*", only 4 last digits will be visible.
   *
   * @param phoneNumber with a format +430123456789 or +43 012 3456789
   * @return a masked number like *******6789
   */
  private maskPhoneNumber(phoneNumber: string): string {
    const length = phoneNumber.length;
    const maskedLength = length - 4;
    const lastDigits = phoneNumber.substring(length - 4, length);
    return '*'.repeat(Math.max(0, maskedLength)) + lastDigits;
  }

  /**
   * Initializes the form with a country code.
   *
   * The aim is to initialize the form with an initial country code value, not `null`.
   * Initializing an empty form and then updating it a moment later is bad UX (imo).
   */
  private initializeForm(): void {
    this.getCountryCode().pipe().subscribe((code) => {
      this.phoneForm = this.fb.group({
        code: new FormControl<number>(code),
        phone: new FormControl<number>(null),
      });
    });
  }

  private disableVerifyButton(): void {
    this.isVerifyDisabled = true;
  }

  private enableVerifyButton(): void {
    this.verifyButton.stopSpinner();
    this.isVerifyDisabled = false;
  }

  /**
   * Fetches the country calling code from an external API.
   * Returns a valid country code without the "+" or null.
   */
  private getCountryCode(): Observable<number> {
    return this.phoneService.getCountryCode().pipe(
      timeout(1500),
      catchError(() => of(null)),
      map((code: string) => {
        const stringCode = code?.replace('+', '').slice(0, 4); // remove "+" from the country code, limit do 4 digits
        const numericCode = Number(stringCode);
        return isNaN(numericCode) ? null : numericCode;
      }),
    );
  }
}

type PhoneGroup = FormGroup<{
  code: FormControl<number>;
  phone: FormControl<number>;
}>;
